import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { restoreBannerCopy } from "@/lib/blog/history-model";
import { publishConfirmCopy } from "@/lib/blog/editor-model";

interface RestoreBannerProps {
  restoredVersion: number;
  sourceVersion: number;
  localeCount: number;
  publishing: boolean;
  onPublish: () => void;
  onDismiss: () => void;
}

/**
 * Restored, not published (Q18).
 *
 * Publish is deliberately **not** the primary action here and always sits
 * behind its own confirm. Restoring is cheap and reversible; publishing every
 * locale is neither, and a primary button one click after a restore invites
 * exactly the accident the append-only history was designed to survive.
 */
export function RestoreBanner({
  restoredVersion,
  sourceVersion,
  localeCount,
  publishing,
  onPublish,
  onDismiss,
}: RestoreBannerProps) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="rounded-2xl border bg-muted/40 p-4" data-testid="restore-banner">
      <h4 className="text-sm font-semibold">Restored, not published</h4>
      <p className="mt-1 max-w-prose text-sm text-muted-foreground">
        {restoreBannerCopy({ restoredVersion, sourceVersion })}
      </p>
      <div className="mt-3 flex items-center gap-2">
        {/* Non-primary, on purpose. */}
        <Button
          variant="outline"
          size="sm"
          disabled={publishing}
          onClick={() => {
            setConfirming(true);
          }}
        >
          Publish this restore
        </Button>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent data-testid="restore-publish-confirm">
          <DialogHeader>
            <DialogTitle>Publish the restored revision?</DialogTitle>
            <DialogDescription>{publishConfirmCopy(localeCount)}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirming(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirming(false);
                onPublish();
              }}
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
